import { getSupabase } from "@/lib/supabase/client";
import { supabase } from "@/lib/supabaseClient";

export interface Partner {
  id: string;
  name: string;
  specialty: string;
  category: string;
  location: string;
  description: string;
  website: string;
  discount_code: string;
  rating: number;
  project_count: number;
  experience: string;
  main_image: string | null;
  views: number;
  website_clicks: number;
  intro_requests: number;
  created_at: string;
  pdf_url?: string;
  updated_at: string;
}

export interface ActivityLog {
  id: string;
  partner_id: string;
  partner_name: string;
  action: "view" | "website_click" | "intro_request";
  timestamp: string;
}

export interface CreatePartnerDTO {
  name: string;
  specialty: string;
  category: string;
  location: string;
  description: string;
  website: string;
  discount_code: string;
  rating?: number;
  project_count?: number;
  experience?: string;
  main_image?: string;
  pdf_url?: string;
}

export const uploadPartnerImage = async (
  file: File,
  partnerId: string,
): Promise<string> => {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session) {
      throw new Error("You must be logged in to upload images");
    }

    const fileExt = file.name.split(".").pop();
    const fileName = `${partnerId}/${Date.now()}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from("partner-images")
      .upload(fileName, file, {
        cacheControl: "3600",
        upsert: false,
      });

    if (uploadError) {
      console.error("Upload error:", uploadError);
      throw uploadError;
    }

    const {
      data: { publicUrl },
    } = supabase.storage.from("partner-images").getPublicUrl(fileName);

    return publicUrl;
  } catch (error) {
    console.error("Error uploading image:", error);
    throw new Error(`Failed to upload image: ${error}`);
  }
};
export const deletePartnerImage = async (imageUrl: string): Promise<void> => {
  try {
    const urlParts = imageUrl.split("/");
    const fileName = urlParts[urlParts.length - 1];
    const folderName = urlParts[urlParts.length - 2];

    const { error } = await supabase.storage
      .from("partner-images")
      .remove([`${folderName}/${fileName}`]);

    if (error) throw error;
  } catch (error) {
    console.error("Error deleting image:", error);
    throw error;
  }
};

export const ensurePartnerImagesBucket = async (): Promise<boolean> => {
  try {
    const { data: buckets, error } = await supabase.storage.listBuckets();

    if (error) throw error;

    const partnerBucket = buckets?.find((b) => b.name === "partner-images");

    if (!partnerBucket) {
      console.log('Bucket "partner-images" not found. Creating...');
      return false;
    }

    return true;
  } catch (error) {
    console.error("Error checking bucket:", error);
    return false;
  }
};

export async function getPartners(
  search = "",
  category = "",
): Promise<Partner[]> {
  let query = supabase
    .from("partners")
    .select("*")
    .order("created_at", { ascending: false });

  if (search) {
    query = query.or(
      `name.ilike.%${search}%,category.ilike.%${search}%,experience.ilike.%${search}%`,
    );
  }

  if (category) {
    query = query.eq("category", category);
  }

  const { data, error } = await query;

  if (error) {
    console.error("Error fetching partners:", error);
    throw error;
  }

  return data;
}

export async function getPartner(id: string): Promise<Partner | null> {
  const { data, error } = await supabase
    .from("partners")
    .select("*")
    .eq("id", id)
    .single();

  if (error) {
    console.error("Error fetching partner:", error);
    throw error;
  }

  return data;
}

export async function createPartner(
  partnerData: CreatePartnerDTO,
): Promise<Partner> {
  const { data, error } = await supabase
    .from("partners")
    .insert([
      {
        name: partnerData.name,
        specialty: partnerData.specialty,
        category: partnerData.category,
        location: partnerData.location,
        description: partnerData.description,
        website: partnerData.website,
        discount_code: partnerData.discount_code,
        rating: partnerData.rating || 0,
        project_count: partnerData.project_count || 0,
        experience: partnerData.experience || "",
        main_image: partnerData.main_image || null,
        pdf_url: partnerData.pdf_url,
        views: 0,
        website_clicks: 0,
        intro_requests: 0,
      },
    ])
    .select()
    .single();

  if (error) {
    console.error("Error creating partner:", error);
    throw error;
  }

  return data;
}

export async function updatePartner(
  id: string,
  partnerData: Partial<Partner>,
): Promise<Partner> {
  const updates: any = {
    name: partnerData.name,
    specialty: partnerData.specialty,
    category: partnerData.category,
    location: partnerData.location,
    description: partnerData.description,
    website: partnerData.website,
    discount_code: partnerData.discount_code,
    rating: partnerData.rating,
    project_count: partnerData.project_count,
    experience: partnerData.experience,
    main_image: partnerData.main_image,
    pdf_url: partnerData.pdf_url,
    updated_at: new Date().toISOString(),
  };

  Object.keys(updates).forEach(
    (key) => updates[key] === undefined && delete updates[key],
  );

  const { data, error } = await supabase
    .from("partners")
    .update(updates)
    .eq("id", id)
    .select()
    .single();

  if (error) {
    console.error("Error updating partner:", error);
    throw error;
  }

  return data;
}

export async function deletePartner(id: string): Promise<void> {
  const partner = await getPartner(id);

  if (partner?.main_image) {
    try {
      await deletePartnerImage(partner.main_image);
    } catch (error) {
      console.error("Error deleting partner image:", error);
    }
  }

  const { error } = await supabase.from("partners").delete().eq("id", id);

  if (error) {
    console.error("Error deleting partner:", error);
    throw error;
  }
}

export async function incrementViews(partnerId: string): Promise<void> {
  const { error } = await supabase.rpc("increment_views", {
    partner_id: partnerId,
  });

  if (error) {
    console.error("Error incrementing views:", error);
    throw error;
  }
}

export async function incrementWebsiteClicks(partnerId: string): Promise<void> {
  const { error } = await supabase.rpc("increment_website_clicks", {
    partner_id: partnerId,
  });

  if (error) {
    console.error("Error incrementing website clicks:", error);
    throw error;
  }
}

export async function incrementIntroRequests(partnerId: string): Promise<void> {
  const { error } = await supabase.rpc("increment_intro_requests", {
    partner_id: partnerId,
  });

  if (error) {
    console.error("Error incrementing intro requests:", error);
    throw error;
  }
}

export async function getRecentActivity(limit = 5): Promise<ActivityLog[]> {
  const { data, error } = await supabase
    .from("activity_logs")
    .select("*")
    .order("timestamp", { ascending: false })
    .limit(limit);

  if (error) {
    console.error("Error fetching activity logs:", error);
    throw error;
  }

  return data;
}

export async function logActivity(
  partnerId: string,
  partnerName: string,
  action: ActivityLog["action"],
  user?: { id: string; email?: string | null } | null,
): Promise<void> {
  const userEmail = user?.email || "anonymous";
  const userId = user?.id || null;

  supabase
    .from("activity_logs")
    .insert([
      {
        partner_id: partnerId,
        partner_name: partnerName,
        action,
        user_id: userId,
        user_email: userEmail,
        timestamp: new Date().toISOString(),
      },
    ])
    .then(({ error }) => {
      if (error) console.error("Error logging activity:", error);
    });
}

export const trackPartnerEvent = (
  partner: Partner,
  action: ActivityLog["action"],
  rpcMethod: string,
  user?: any,
) => {
  Promise.all([
    supabase.rpc(rpcMethod, { partner_id: partner.id }),
    logActivity(partner.id, partner.name, action, user),
  ]).catch((err) => console.error(`Tracking failed for ${action}:`, err));
};

export function subscribeToPartners(callback: (payload: any) => void) {
  const channel = supabase
    .channel("partners-changes")
    .on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "partners",
      },
      callback,
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

export function subscribeToActivity(callback: (payload: any) => void) {
  const channel = supabase
    .channel("activity-changes")
    .on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "activity_logs",
      },
      callback,
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

export async function uploadPartnerPDF(
  file: File,
  partnerName: string,
): Promise<string> {
  const supabase = getSupabase();

  const fileExt = file.name.split(".").pop();
  const cleanPartnerName = partnerName.replace(/\s+/g, "-").toLowerCase();
  const fileName = `${Date.now()}-${cleanPartnerName}.${fileExt}`;

  const filePath = `portfolios/${fileName}`;

  const { data, error: uploadError } = await supabase.storage
    .from("partner-documents")
    .upload(filePath, file, {
      cacheControl: "3600",
      upsert: true,
    });

  if (uploadError) {
    console.error("Error uploading PDF:", uploadError);
    throw new Error("Failed to upload PDF to storage");
  }

  const {
    data: { publicUrl },
  } = supabase.storage.from("partner-documents").getPublicUrl(filePath);

  return publicUrl;
}

export async function deletePartnerPDF(fileUrl: string): Promise<void> {
  const supabase = getSupabase();

  try {
    const urlParts = fileUrl.split("/");
    const fileName = urlParts[urlParts.length - 1];

    if (!fileName) {
      throw new Error("Invalid file URL");
    }

    const filePath = `portfolios/${fileName}`;

    const { error } = await supabase.storage
      .from("partner-documents")
      .remove([filePath]);

    if (error) {
      console.error("Error deleting PDF:", error);
      throw new Error("Failed to delete PDF from storage");
    }

    console.log(`PDF ${fileName} deleted successfully`);
  } catch (error) {
    console.error("Error in deletePartnerPDF:", error);
    throw error;
  }
}
